import {
  Component, OnInit, OnDestroy, Output, PLATFORM_ID, EventEmitter, ChangeDetectionStrategy,
  Signal, signal, inject
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';

import { OrderHistoryList } from '@spartacus/order/root';

import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { filter, switchMap, tap, map } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';

import { IOrderDetails, SdOrderDetailsComponent } from '@seb/web-design-system/account';
import { SdButtonComponent, SdTitleComponent, StatusEnum } from '@seb/web-design-system/core';

import { CustomOrder } from '@seb/web-common/core/models';
import { OrderStatusEnum, OrderTypeEnum } from '@seb/web-common/core/enums';
import { OrderMapper } from '@seb/web-common/core/mappers';
import { BaseStoreService, ConfigurationService, TrackingService, ToasterService } from '@seb/web-common/core/services';
import { OrderFacade } from '@seb/web-common/core/facades';

@Component({
  selector: 'wc-my-orders',
  templateUrl: './my-orders.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdTitleComponent,
    SdOrderDetailsComponent,
    SdButtonComponent,
    TranslatePipe
  ]
})
export class MyOrdersComponent implements OnInit, OnDestroy {
  currentOrders = signal<IOrderDetails[]>([]);
  pastOrders = signal<IOrderDetails[]>([]);
  hasNext = signal(false);
  orderCode: Signal<string>;
  shipupReturnPage = signal('');
  isLoading = signal(true);

  private readonly sisInvoiceEnabled = signal(false);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly subscriptions = new Subscription();

  private readonly CURRENT_ORDER_STATUSES = [
    OrderStatusEnum.IN_PREPARATION,
    OrderStatusEnum.VALIDATED,
    OrderStatusEnum.PARTIALLY_DISPATCH
  ];
  private readonly PAST_ORDER_STATUSES = [
    OrderStatusEnum.SHIPPED,
    OrderStatusEnum.DELETED,
    OrderStatusEnum.CANCELED,
    OrderStatusEnum.CANCELLED,
    OrderStatusEnum.PARTIALLY_SHIPPED_CANCELLED,
    OrderStatusEnum.COMPLETED
  ];
  private readonly ELIGIBLE_RETURN_STATUSES = [
    OrderStatusEnum.PARTIALLY_DISPATCH,
    OrderStatusEnum.PARTIALLY_SHIPPED_CANCELLED,
    OrderStatusEnum.SHIPPED,
    OrderStatusEnum.COMPLETED
  ];
  private readonly CURRENT_ORDERS_PAGE_SIZE = 100;
  private readonly PAST_ORDERS_PAGE_SIZE = 3;
  private readonly pastOrderHistoryList$ = new BehaviorSubject<OrderHistoryList>(undefined);
  private currentPage = 0;

  private currentOrdersAlreadyLoaded = false;

  @Output()
  readonly openRecommendedPriceModal = new EventEmitter<void>();

  constructor(
    private readonly orderService: OrderFacade,
    private readonly orderMapper: OrderMapper,
    private readonly route: ActivatedRoute,
    private readonly baseStoreService: BaseStoreService,
    private readonly configService: ConfigurationService,
    private readonly trackingService: TrackingService,
    private readonly toasterService: ToasterService
  ) {
    this.orderCode = toSignal(
      this.route.queryParams.pipe(map((params) => params['orderCode'] ?? '')),
      { initialValue: '' }
    );
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.initializeConfiguration();

      this.subscriptions.add(
        this.orderService
          .getOrderHistoryList(this.PAST_ORDERS_PAGE_SIZE, 0, undefined, this.PAST_ORDER_STATUSES)
          .subscribe((history) => this.pastOrderHistoryList$.next(history))
      );

      const currentOrders$ = this.createCurrentOrdersStream();
      const pastOrders$ = this.createPastOrdersStream();

      this.subscriptions.add(
        combineLatest([currentOrders$, pastOrders$])
          .subscribe(([currentOrders, pastOrders]) => {
            const baseStore = this.baseStoreService.baseStore;
            this.shipupReturnPage.set(baseStore.returnPageUrl);

            this.mapCurrentOrders(currentOrders, baseStore);
            this.mapPastOrders(pastOrders, baseStore);

            this.currentOrdersAlreadyLoaded = true;
            this.isLoading.set(false);
          })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getNextPage(): void {
    this.isLoading.set(true);
    this.subscriptions.add(
      this.orderService
        .getOrderHistoryList(this.PAST_ORDERS_PAGE_SIZE, this.currentPage + 1, undefined, this.PAST_ORDER_STATUSES)
        .subscribe((history) => this.pastOrderHistoryList$.next(history))
    );
  }

  onOpenRecommendedPriceModal(): void {
    this.openRecommendedPriceModal.emit();
  }

  toShipupReturnPage(orderCode: string): void {
    if (isPlatformBrowser(this.platformId)) {
      window.open(this.shipupReturnPage(), ' blank');
    }
    this.trackingService.sendEvent('clickRefund', {
      orderID: orderCode
    });
  }

  downloadInvoice(orderCode: string): void {
    this.orderService.getInvoiceOrder(orderCode).subscribe({
      next: ({ blob, filename }) => {
        const a = document.createElement('a');
        const objectUrl = URL.createObjectURL(blob);
        a.href = objectUrl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(objectUrl);
      },
      error: () => {
        this.toasterService.sendToasterEvent({
          title: 'invoice.toasts.error.title',
          description: 'invoice.toasts.error.description',
          status: StatusEnum.error
        });
      }
    });
  }

  private initializeConfiguration(): void {
    const config = this.configService.config;
    const isInvoiceEnabled = config?.invoice?.sis?.enabled ?? false;

    this.shipupReturnPage.set(config?.shipup?.returnPageUrl);
    this.sisInvoiceEnabled.set(isInvoiceEnabled);
  }

  private createCurrentOrdersStream(): Observable<CustomOrder[]> {
    return this.orderService
      .getOrderHistoryList(this.CURRENT_ORDERS_PAGE_SIZE, 0, undefined, this.CURRENT_ORDER_STATUSES)
      .pipe(
        tap((currentOrdersHistory) => {
          this.currentOrders.set(currentOrdersHistory.orders.map((order) =>
            this.orderMapper.orderHistoryToIOrderDetails(order)
          ));
        }),
        switchMap((currentOrdersHistory) => this.fetchOrdersWithDetails(currentOrdersHistory))
      );
  }

  private createPastOrdersStream(): Observable<CustomOrder[]> {
    return this.pastOrderHistoryList$.pipe(
      filter((history) => !!history),
      tap((pastOrdersHistory) => {
        this.currentPage = pastOrdersHistory.pagination.currentPage;
        this.hasNext.set(this.currentPage < pastOrdersHistory.pagination.totalPages - 1);
        if (this.currentPage === 0) {
          this.pastOrders.set([]);
        }
        this.pastOrders.update((orders) => [
          ...orders,
          ...pastOrdersHistory.orders.map((order) => this.orderMapper.orderHistoryToIOrderDetails(order))
        ]);
      }),
      switchMap((pastOrdersHistory) => this.fetchOrdersWithDetails(pastOrdersHistory))
    );
  }

  private fetchOrdersWithDetails(orderHistory: OrderHistoryList): Observable<CustomOrder[]> {
    if (orderHistory.orders.length > 0) {
      const getOrdersWithDetails = orderHistory.orders.map((order) =>
        this.orderService.getOrderDetails(order.code)
      );
      return combineLatest(getOrdersWithDetails);
    }
    return of([]);
  }

  private mapCurrentOrders(orders: CustomOrder[], baseStore: any): void {
    if (!this.currentOrdersAlreadyLoaded) {
      this.currentOrders.set(orders.map((order) =>
        this.orderMapper.orderToIOrderDetails({
          order,
          isTaxesIncluded: baseStore.displayTaxesIncluded,
          isReturnEligible: this.isReturnEligible(order),
          isNewPriceManagementActivated: baseStore.activateNewPriceManagement
        })
      ));
    }
  }

  private mapPastOrders(orders: CustomOrder[], baseStore: any): void {
    orders.forEach((order) => {
      const orderDetails = this.orderMapper.orderToIOrderDetails({
        order,
        isTaxesIncluded: baseStore.displayTaxesIncluded,
        isReturnEligible: this.isReturnEligible(order),
        isNewPriceManagementActivated: baseStore.activateNewPriceManagement,
        hasInvoiceDownload: this.hasInvoiceDownload(order)
      });
      this.pastOrders.update((oldOrders) => {
        const orderIndex = oldOrders.findIndex((o) => o.code === order.code);
        if (orderIndex > -1) {
          const updatedOrders = [...oldOrders];
          updatedOrders[orderIndex] = orderDetails;
          return updatedOrders;
        }
        return oldOrders;
      });
    });
  }

  private isReturnEligible(order: CustomOrder): boolean {
    return (
      this.ELIGIBLE_RETURN_STATUSES.includes(order.status as OrderStatusEnum) &&
      !!this.shipupReturnPage() &&
      !order.isForfait &&
      order.orderType === OrderTypeEnum.EXTLOG
    );
  }

  private hasInvoiceDownload(order: CustomOrder): boolean {
    if (order.orderType === OrderTypeEnum.EXTLOG) return true;
    return !!this.sisInvoiceEnabled();
  }
}
